import { useState } from 'react'

import { useIntl } from '@/intl'

export function useTexts() {
  const { translate } = useIntl()

  const [api] = useState(() => ({
    title: translate({ id: 'modals.routeOptimization.header.title' }),
    titleWithRange: (range: string) =>
      translate({ id: 'modals.routeOptimization.header.titleWithRange', values: { range } }),

    optimizationStrategyLabel: translate({
      id: 'modals.routeOptimization.optimizationStrategy.label',
    }),
    optimizationStrategyTooltip: translate({
      id: 'modals.routeOptimization.optimizationStrategy.tooltip',
    }),
    fleetSavings: translate({ id: 'modals.routeOptimization.strategy.fleetSavings.label' }),
    fleetSavingsDescription: translate({
      id: 'modals.routeOptimization.strategy.fleetSavings.description',
    }),
    fleetSavingsBadge: translate({ id: 'modals.routeOptimization.strategy.fleetSavings.badge' }),
    balancedPerformance: translate({
      id: 'modals.routeOptimization.strategy.balancedPerformance.label',
    }),
    balancedPerformanceDescription: translate({
      id: 'modals.routeOptimization.strategy.balancedPerformance.description',
    }),
    balancedPerformanceBadge: translate({
      id: 'modals.routeOptimization.strategy.balancedPerformance.badge',
    }),
    balancedPerformanceDisabledTooltip: translate({
      id: 'modals.routeOptimization.strategy.balancedPerformance.disabledTooltip',
    }),

    driverUsageLabel: translate({ id: 'modals.routeOptimization.driverUsage.label' }),
    minimizeDrivers: translate({ id: 'modals.routeOptimization.driverUsage.minimizeDrivers' }),
    minimizeDriversDefault: translate({
      id: 'modals.routeOptimization.driverUsage.minimizeDriversDefault',
    }),
    useAllActiveDrivers: translate({
      id: 'modals.routeOptimization.driverUsage.useAllActiveDrivers',
    }),

    balanceByLabel: translate({ id: 'modals.routeOptimization.balanceBy.label' }),
    balanceByHelper: translate({ id: 'modals.routeOptimization.balanceBy.helper' }),
    balanceByRequired: translate({ id: 'modals.routeOptimization.balanceBy.validation.required' }),

    // Labels used by the Build Progress modal's "Optimization configuration" panel.
    configAllDrivers: translate({ id: 'modals.routeOptimization.balanceBy.config.allDrivers' }),
    configMinDrivers: translate({ id: 'modals.routeOptimization.balanceBy.config.minDrivers' }),
    configBalanceOrderDistribution: translate({
      id: 'modals.routeOptimization.balanceBy.config.orderDistribution',
    }),
    configBalanceWorkingTime: translate({
      id: 'modals.routeOptimization.balanceBy.config.workingTime',
    }),
    configBalanceDistance: translate({
      id: 'modals.routeOptimization.balanceBy.config.distance',
    }),
    configBalanceLoadDistribution: (loadName: string) =>
      translate({
        id: 'modals.routeOptimization.balanceBy.config.loadDistribution',
        values: { loadName },
      }),
    balanceByOrders: translate({ id: 'modals.routeOptimization.balanceBy.option.orders' }),
    balanceByOrdersDescription: translate({
      id: 'modals.routeOptimization.balanceBy.option.orders.description',
    }),
    balanceByWorkingTime: translate({
      id: 'modals.routeOptimization.balanceBy.option.workingTime',
    }),
    balanceByWorkingTimeDescription: translate({
      id: 'modals.routeOptimization.balanceBy.option.workingTime.description',
    }),
    balanceByDistance: translate({
      id: 'modals.routeOptimization.balanceBy.option.distance',
    }),
    balanceByDistanceDescription: translate({
      id: 'modals.routeOptimization.balanceBy.option.distance.description',
    }),
    balanceByLoadType: translate({ id: 'modals.routeOptimization.balanceBy.option.loadType' }),
    balanceByLoadTypeDescription: translate({
      id: 'modals.routeOptimization.balanceBy.option.loadType.description',
    }),

    loadTypeLabel: translate({ id: 'modals.routeOptimization.loadType.label' }),
    loadTypeDescription: translate({ id: 'modals.routeOptimization.loadType.description' }),
    loadTypeRequired: translate({ id: 'modals.routeOptimization.loadType.validation.required' }),

    alertMinHoursTitle: translate({
      id: 'modals.routeOptimization.alert.minHours.title',
    }),

    alertMinHoursBody: 'modals.routeOptimization.alert.minHours.body',
    alertMinHoursBodyWithExceptions: 'modals.routeOptimization.alert.minHours.bodyWithExceptions',
    alertMinHoursCheckbox: translate({
      id: 'modals.routeOptimization.alert.minHours.checkbox',
    }),

    alertMinLoadTitle: translate({ id: 'modals.routeOptimization.alert.minLoad.title' }),
    alertMinLoadBody: 'modals.routeOptimization.alert.minLoad.body',
    alertMinLoadBodyWithExceptions: 'modals.routeOptimization.alert.minLoad.bodyWithExceptions',
    alertMinLoadCheckbox: translate({
      id: 'modals.routeOptimization.alert.minLoad.checkbox',
    }),

    alertBothTitle: translate({ id: 'modals.routeOptimization.alert.both.title' }),
    alertBothBody: 'modals.routeOptimization.alert.both.body',
    alertBothBodyWithExceptions: 'modals.routeOptimization.alert.both.bodyWithExceptions',
    alertBothCheckbox: translate({ id: 'modals.routeOptimization.alert.both.checkbox' }),

    strategyExampleTitle: (strategy: string) =>
      translate({ id: 'modals.routeOptimization.example.title', values: { strategy } }),
    strategyExampleDisclaimer: translate({
      id: 'modals.routeOptimization.example.disclaimer',
    }),
    typicalOutcome: translate({ id: 'modals.routeOptimization.example.typicalOutcome' }),
    outcomeVehicleUsage: translate({ id: 'modals.routeOptimization.example.vehicleUsage' }),
    outcomeTravelEfficiency: translate({
      id: 'modals.routeOptimization.example.travelEfficiency',
    }),
    outcomeWorkloadFairness: translate({
      id: 'modals.routeOptimization.example.workloadFairness',
    }),
    outcomeLoadSaturation: translate({ id: 'modals.routeOptimization.example.loadSaturation' }),
    outcomeMin: translate({ id: 'modals.routeOptimization.example.outcome.min' }),
    outcomeMax: translate({ id: 'modals.routeOptimization.example.outcome.max' }),
    outcomeLow: translate({ id: 'modals.routeOptimization.example.outcome.low' }),
    outcomeMedium: translate({ id: 'modals.routeOptimization.example.outcome.medium' }),
    outcomeHigh: translate({ id: 'modals.routeOptimization.example.outcome.high' }),
    sampleVehicle: (letter: string) =>
      translate({ id: 'modals.routeOptimization.example.sampleVehicle', values: { letter } }),

    cancel: translate({ id: 'modals.routeOptimization.footer.cancel' }),
    startOptimization: translate({ id: 'modals.routeOptimization.footer.startOptimization' }),

    toastStarted: translate({ id: 'modals.routeOptimization.toast.started' }),
    toastError: translate({ id: 'modals.routeOptimization.toast.error' }),
  }))

  return api
}
